import './global/jquery-migrate';
import './common/select-option-plugin';
import PageManager from './page-manager';
import quickSearch from './global/quick-search';
import currencySelector from './global/currency-selector';
import mobileMenuToggle from './global/mobile-menu-toggle';
import menu from './global/menu';
import foundation from './global/foundation';
import quickView from './global/quick-view';
import cartPreview from './global/cart-preview';
import privacyCookieNotification from './global/cookieNotification';
import maintenanceMode from './global/maintenanceMode';
import carousel from './common/carousel';
import loadingProgressBar from './global/loading-progress-bar';
import svgInjector from './global/svg-injector';
import quickAddSkus from './f/quick-add-sku';
import accountMenu from './f/account-menu';
import autoHighlight from './f/auto-highlight';
import cardAddToCart from './f/card-add-to-cart';
import hoverNavigation from './f/hover-navigation';
import recentSellers from './f/recent-sellers';
import initExpressCheckoutTracking from './f/express-checkout-tracking';
import ordergrooveLogout from './global/ordergroove-logout';
import {onlineServiceAnalyticsResourceBuilderFile, processEventFile, buildOnlineServiceAnalyticsEventFile} from './custom/event/onlineServiceAnalyticsResourceBuilder';
//import addToCartGraphQL from './global/graphQl-add-to-cart';
import CategoryListing from './global/listingPagesLoaded';
import {addToCartSlideOut} from './custom/event/addToCartSlideOut';
import { setWithExpiry, getWithExpiry } from './common/utils/storage-utils';
import { getCookie, setCookie, deleteCookie, parseConsentCookieStringToObject } from './common/utils/cookie-utils';
import { getNavigationType, getDeviceType } from './common/utils/browser-utils';
import { parseBoolean, getAddToCartData, getAddToCartProductDetails } from './f/atc-analytics';
import { getReviewStars, getReviewStarsArray, isValidLoginPassword } from './common/utils/review-utils';
import { pageLoadErrorEvents, getDefaultData } from './custom/event/event-loop';
import { sendOnlineServiceAnalyticsEvent, saveVisitQueryParams, onSearchPageContentsChange, getPageProductIds, initAnalyticsSenderListeners } from './custom/event/analytics-sender';
import { initConsentTracking } from './custom/event/consent-tracking';
import { initRemoteLogging, testRemoteLogging } from './common/utils/logger';
import { setupObserver, initAccountIframe } from './custom/account/account-iframe';
import { getTealiumUtagVariables } from './custom/tealium/tealium-orchestrator';
import { initializeUtagDataWithCustomer, tealiumGetCartItemsNames, breadcrumbToCategories } from './custom/tealium/tealium-page-processors';
import { GetSearchResults as getTealiumSearchResults, getMostRepresentedCategory as getTealiumMostRepresentedCategory, getLatestSearchFromCache } from './custom/event/search-utils';

// --- Begin: BCRecoObserverClass + singleton attachment ---
import { bus, Events } from './common/event-bus';
(function (win) {
    'use strict';
    if (!win) return;

    class BCRecoObserver {
        /**
         * @param {Object} options
         * @param {string} [options.containerSelector='.ss__recommendation']
         * @param {string} [options.readySelector='.ss__carousel.ss__carousel__recommendation']
         * @param {boolean} [options.runOnce=true]
         * @param {number} [options.debounceMs=50]
         * @param {function} [options.onReady] - function(containerEl, carouselEl)
         * @param {boolean} [options.emitEvent=true] - dispatch CustomEvent('bc:reco:ready') on container
         */
        constructor(options = {}) {
            this.defaults = {
                containerSelector: '.ss__recommendation',
                readySelector: '.ss__carousel.ss__carousel__recommendation',
                runOnce: true,
                debounceMs: 50,
                onReady: null,
                emitEvent: true,
            };

            this.options = { ...this.defaults, ...options };

            // Internal state
            this.running = false;
            this.globalObserver = null;
            this.perNodeObservers = new WeakMap(); // container -> MutationObserver
            this.observed = new WeakSet();         // containers with per-node observer
            this.handled = new WeakSet();          // containers that have fired when runOnce=true
            this.debouncers = new WeakMap();       // container -> timeout

            // Bind callbacks
            this._onMutations = this._onMutations.bind(this);
        }

        // ---------- Public API ----------
        init(opts = {}) {
            this.setOptions(opts);
            if (this.running) return;

            this.running = true;
            this._whenDOMReady(() => {
                this._observeExistingContainers();
                this._attachGlobalObserver();
            });
        }

        destroy() {
            if (!this.running) return;
            this.running = false;
            this._detachGlobalObserver();
            this._resetPerNodeObservers();
        }

        isRunning() {
            return !!this.running;
        }

        setOptions(opts = {}) {
            this.options = { ...this.options, ...opts };
            if (this.running) {
                // Re-check in case onReady/selector changed
                this.checkNow();
            }
        }

        checkNow() {
            this._qsa(document, this.options.containerSelector).forEach((c) => this._handleIfReady(c));
        }

        // ---------- Private helpers ----------
        _whenDOMReady(cb) {
            if (document.readyState === 'loading') {
                document.addEventListener('DOMContentLoaded', cb, { once: true });
            } else {
                cb();
            }
        }

        _qs(root, sel) { return root.querySelector(sel); }
        _qsa(root, sel) { return Array.from(root.querySelectorAll(sel)); }

        _findCarousel(container) {
            return this._qs(container, this.options.readySelector);
        }

        _debounceContainer(container, fn, delay = this.options.debounceMs) {
            const prev = this.debouncers.get(container);
            if (prev) clearTimeout(prev);
            const t = setTimeout(() => fn(container), delay);
            this.debouncers.set(container, t);
        }

        _handleIfReady(container) {
            const carousel = this._findCarousel(container);
            if (!carousel) return false;

            if (this.options.runOnce && this.handled.has(container)) return true;

            if (this.options.runOnce) {
                this.handled.add(container);
                const ob = this.perNodeObservers.get(container);
                if (ob) ob.disconnect();
            }

            try {
                if (typeof this.options.onReady === 'function') {
                    this.options.onReady(container, carousel);
                }
                if (this.options.emitEvent !== false) {
                    container.dispatchEvent(new CustomEvent('bc:reco:ready', {
                        detail: { container, carousel },
                        bubbles: false,
                    }));
                    // Also emit on the typed event bus for new listeners
                    bus.emit(Events.RECO_READY, { container, carousel }, container);
                }
            } catch (e) {
                console.error('[BCRecoObserver] onReady error:', e);
            }
            return true;
        }

        _observeContainer(container) {
            if (this.observed.has(container)) return;
            this.observed.add(container);

            // Initial pass in case it's already ready
            this._handleIfReady(container);

            const ob = new MutationObserver(() => {
                this._debounceContainer(container, (c) => this._handleIfReady(c));
            });

            ob.observe(container, {
                childList: true,
                subtree: true,
                // If readiness is a class toggle only, uncomment:
                // attributes: true,
                // attributeFilter: ['class'],
            });

            this.perNodeObservers.set(container, ob);
        }

        _observeExistingContainers() {
            this._qsa(document, this.options.containerSelector).forEach((c) => this._observeContainer(c));
        }

        _onMutations(mutationList) {
            for (const m of mutationList) {
                for (const node of m.addedNodes) {
                    if (!node || node.nodeType !== 1) continue;
                    const el = /** @type {Element} */ (node);

                    // Container added directly
                    if (el.matches?.(this.options.containerSelector)) {
                        this._observeContainer(el);
                    }
                    // Containers nested in added subtree
                    el.querySelectorAll?.(this.options.containerSelector)?.forEach((c) => this._observeContainer(c));

                    // Carousel added; link back to nearest container
                    if (el.matches?.(this.options.readySelector) || el.querySelector?.(this.options.readySelector)) {
                        const carousel = el.matches?.(this.options.readySelector)
                            ? el
                            : el.querySelector(this.options.readySelector);
                        const container = carousel?.closest?.(this.options.containerSelector);
                        if (container) {
                            this._observeContainer(container);
                            this._handleIfReady(container);
                        }
                    }
                }
            }
        }

        _attachGlobalObserver() {
            if (this.globalObserver) return;
            this.globalObserver = new MutationObserver(this._onMutations);
            this.globalObserver.observe(document.body, { childList: true, subtree: true });
        }

        _detachGlobalObserver() {
            if (!this.globalObserver) return;
            this.globalObserver.disconnect();
            this.globalObserver = null;
        }

        _resetPerNodeObservers() {
            for (const [container, ob] of this.perNodeObservers.entries()) {
                try { ob.disconnect(); } catch (_) { }
                this.perNodeObservers.delete(container);
            }
            this.observed = new WeakSet();
            this.handled = new WeakSet();
            this.debouncers = new WeakMap();
        }
    }

    // Expose class and a convenient singleton
    if (!win.BCRecoObserverClass) {
        win.BCRecoObserverClass = BCRecoObserver;
    }
    if (!win.BCRecoObserver) {
        win.BCRecoObserver = new BCRecoObserver();
    }
})(window);
// --- End: BCRecoObserverClass + singleton attachment ---


export default class Global extends PageManager {
    onReady() {
        cartPreview(this.context.secureBaseUrl, this.context.cartId, this.context.disableMinicart, this.context.enableSideCart);
        quickSearch();
        currencySelector();
        foundation($(document));
        quickView(this.context);
        //CategoryListing(this.context);
        carousel();
        mobileMenuToggle();
        privacyCookieNotification();
        maintenanceMode(this.context.maintenanceMode);
        loadingProgressBar();
        quickAddSkus();
        autoHighlight();
        cardAddToCart();
        svgInjector();
        accountMenu();
        initExpressCheckoutTracking();
        ordergrooveLogout();

        if (document.getElementById('ReviewsWidget')) {
            import('./f/reviews-search-guard').then(({ default: initReviewsSearchGuard }) => {
                initReviewsSearchGuard();
            });
        }

        if (document.querySelector('[data-loyalty-cart-panel], [data-loyalty-drawer-panel]')) {
            import('./custom/loyalty/cart-panel').then(({ default: initLoyaltyCartPanel }) => {
                initLoyaltyCartPanel(this.context);
            });
        }

        try {
            const eventsCtx = window.BC_CONTEXT && window.BC_CONTEXT.events;
            if (eventsCtx && eventsCtx.code
                && (localStorage.getItem('eventDiscountClaim') || eventsCtx.claim)) {
                import('./f/event-discount').then(({ default: initEventDiscount }) => {
                    initEventDiscount();
                });
            }
        } catch (e) { /* storage blocked — no claim readable, no chunk to load */ }

        const favoritesCtx = window.BC_CONTEXT && window.BC_CONTEXT.favorites;
        if (favoritesCtx && favoritesCtx.enabled && favoritesCtx.storeWishlist) {
            import('./f/favorites').then(({ default: initFavorites }) => {
                initFavorites(this.context);
            });
        }

        const categoryListing = new CategoryListing(this.context);
        //window.listingPagesLoaded = categoryListing.listingPagesLoaded.bind(categoryListing);
        window.listingPagesLoaded = categoryListing.listingPagesLoaded;
        window.addToCartSlideOut = addToCartSlideOut;

        // Utility modules — exposed as globals for any code that calls them
        // after the bundle loads. The inline versions in base.html partials cover
        // the early-load window before the bundle is ready.
        window.setWithExpiry = setWithExpiry;
        window.getWithExpiry = getWithExpiry;
        window.getCookie = getCookie;
        window.setCookie = setCookie;
        window.deleteCookie = deleteCookie;
        window.parseConsentCookieStringToObject = parseConsentCookieStringToObject;
        window.getNavigationType = getNavigationType;
        window.getDeviceType = getDeviceType;
        // Tier-1 webpack extractions (Phase 3)
        window.parseBoolean = parseBoolean;
        window.getAddToCartData = getAddToCartData;
        window.getAddToCartProductDetails = getAddToCartProductDetails;
        window.getReviewStars = getReviewStars;
        window.getReviewStarsArray = getReviewStarsArray;
        window.isValidLoginPassword = isValidLoginPassword;
        window.pageLoadErrorEvents = pageLoadErrorEvents;
        window.getDefaultData = getDefaultData;
        window.sendOnlineServiceAnalyticsEvent = sendOnlineServiceAnalyticsEvent;
        window.saveVisitQueryParams = saveVisitQueryParams;
        window.onSearchPageContentsChange = onSearchPageContentsChange;
        window.getPageProductIds = getPageProductIds;
        window.setupObserver = setupObserver;
        // Page-view payload builder helpers (consumed by the onlineService SPA page_view + listing pages)
        window.initializeUtagDataWithCustomer = initializeUtagDataWithCustomer;
        window.getLatestSearchFromCache = getLatestSearchFromCache;
        window.tealiumGetCartItemsNames = tealiumGetCartItemsNames;
        window.breadcrumbToCategories = breadcrumbToCategories;
        window.GetSearchResults = getTealiumSearchResults;
        window.getMostRepresentedCategory = getTealiumMostRepresentedCategory;
        // Build the onlineService page_view payload (sets window.basePageViewEvent).
        // De-Tealium: no longer assigned to a window.utag_data global.
        getTealiumUtagVariables();
        // Stamp the search-results count onto basePageViewEvent directly (no utag_data round-trip).
        const _searchTotal = getTealiumSearchResults();
        if (window.basePageViewEvent && _searchTotal > 0) {
            window.basePageViewEvent.search_results = String(_searchTotal);
        }
        // Populate most_represented_category from SS category facets (DOM may already be ready)
        const _topCat = getTealiumMostRepresentedCategory();
        if (_topCat && window.basePageViewEvent) {
            window.basePageViewEvent.most_represented_category = _topCat;
        }
        // Stamp navigation type onto the base event
        if (window.basePageViewEvent) {
            window.basePageViewEvent.navigation_type = typeof window.getNavigationType === 'function' ? window.getNavigationType() : '';
        }
        // Register event listeners that were previously inline in analytics-sender.html
        initAnalyticsSenderListeners(pageLoadErrorEvents);
        // Initialize remote logging (forwards warn/error to backend api/clientlog)
        initRemoteLogging();
        window.testRemoteLogging = testRemoteLogging;
        // Initialize account iframe observer (replaces DOMContentLoaded listener in account-iframe.html)
        initAccountIframe();
        window.onlineServiceAnalyticsResourceBuilderFile = onlineServiceAnalyticsResourceBuilderFile;
        window.processEventFile = processEventFile;
        // Drain any events that were queued before the bundle loaded
        if (window._eventQueue && window._eventQueue.length) {
            window._eventQueue.forEach(({ newEvent, eventData }) => processEventFile(newEvent, eventData));
            window._eventQueue = [];
        }
        window.buildOnlineServiceAnalyticsEventFile = buildOnlineServiceAnalyticsEventFile;
        if (!/Mobi/i.test(navigator.userAgent) && this.context.enableRecentSellers) {
            recentSellers(this.context.recentSellersTimer, this.context.recentSellersHideAfter, this.context);
        }

        if (!/Mobi/i.test(navigator.userAgent) && this.context.enableHoverNavigation) {
            hoverNavigation();
        } else {
            menu();
        }

        if (window.ApplePaySession) {
            $('.apple-pay-checkout-button').show();
        }
        onlineServiceAnalyticsResourceBuilderFile();
        initConsentTracking();

        // === Simple namespaced logger (toggle with ?debugReco=1 or window.DEBUG_RECO=true) ===


        
        // ---- Single‑flight registry  ----
        /**
         * We keep this on window to survive hot reloads / re-inits and to ensure
         * a single instance even if the file is evaluated more than once.
         */
        window.__recAccREG = window.__recAccREG || {
        inFlight: false,
        lastSig: null,
        currentId: null,
        abortController: null,
        };


        // Initialize the recommender observer
        if (window?.BCRecoObserver?.init) {
            //console.info("[BCRecoObserver] Initializing...");

            // Track performance start
            const recoInitStart = performance.now();

            window.BCRecoObserver.init({
                runOnce: true,
                containerSelector: ".ss__recommendation",
                readySelector: ".ss__carousel.ss__carousel__recommendation",
                onReady(containerEl, carouselEl) {
                    const recoReadyTime = performance.now();
                    const durationMs = Math.round(recoReadyTime - recoInitStart);

                    if (!containerEl || !carouselEl) {
                        //console.warn("[BCRecoObserver] onReady called but container or carousel missing", {containerEl,carouselEl,durationMs,});
                        return;
                    }

                    //console.debug("[BCRecoObserver] onReady triggered", {container: containerEl,carousel: carouselEl,durationMs,});                    });

                    // ---- Guard functions ----
                    function findRecommenderContainer(element) {
                        if (!element) return null;
                        let current = element.parentElement;
                        while (current) {
                            if (current.id?.startsWith("ss__recommendation--")) {
                                return current;
                            }
                            current = current.parentElement;
                        }
                        return null;
                    }

                    function getSku(el) {
                        if (!el) return null;
                        let article = el.closest("article[data-original-sku-text]");
                        if (!article) {
                            article = el.closest("article.card");
                            if (article) {
                                const star_container = article.querySelector("a.star_container");
                                return star_container?.classList?.[1] || null;
                            }
                            return null;
                        }
                        // Try different SKU text locations
                        const skuDisplay = article.querySelector(
                            "article > div.card-section.card-section--body.ss__result__details > div.card-brandwrap > p"
                        )?.textContent;
                        if (skuDisplay) {
                            return skuDisplay.replace(/^SKU:\s*/i, "").trim();
                        }
                        return (
                            article.querySelector("article > div.card-section.card-section--body > div.card-brandwrap > p")
                                ?.textContent.replace(/^SKU:\s*/i, "")
                                .trim() || null
                        );
                    }

                    // ---- Collect SKUs ----
                    const skuList = [];
                    carouselEl.querySelectorAll(".card").forEach((item) => {
                        const skuElement = getSku(item);
                        if (skuElement) skuList.push(skuElement);
                    });
                    //console.debug("[BCRecoObserver] Extracted SKUs", {skuList,skuCount: skuList.length,durationMs,});

                    // ---- Find recommender name ----
                    let recommenderName = null;
                    const recommenderElement = findRecommenderContainer(carouselEl);

                    if (recommenderElement) {
                        const titleEl = recommenderElement.querySelector("h3") || recommenderElement.closest("h3");
                        recommenderName = titleEl?.textContent?.trim() || null;
                    }

                    if (!recommenderName) {
                        const fallbackTitle = carouselEl.closest("h2") || document.querySelector("h2.productView-title");
                        recommenderName = fallbackTitle?.textContent?.trim() || null;
                    }

                    if (!recommenderName) {
                        //console.warn("[BCRecoObserver] Could not determine recommender name", { carouselEl, durationMs });
                    }

                    // ---- Analytics event ----
                    const newEvent = {
                        loadNumber: 1,
                        scriptLoaded: null,
                        interactionsCompleted: null,
                        errorMessage: {
                            eventName: "reco_ready",
                            eventMessage: "",
                            timeStamp: new Date().toISOString(),
                        },
                    };

                    const pageType = typeof getPageType === "function" ? getPageType() : "unknown";
                    const gcardId = typeof getCartId === "function" ? getCartId() : null;

                    const eventObject = {
                        tealium_event: "reco_ready",
                        skus: skuList.join(",") || null,
                        page_type: pageType,
                        recommender: "recommender",
                        recommenderName,
                        durationMs,
                    };

                    if (typeof buildOnlineServiceAnalyticsEventFile === "function") {
                        buildOnlineServiceAnalyticsEventFile(newEvent, eventObject, gcardId);
                        //console.debug("[BCRecoObserver] Analytics event built", { eventObject, durationMs });
                    } else {
                        //console.error("[BCRecoObserver] Missing buildOnlineServiceAnalyticsEventFile()");
                    }

                    // ---- Data Layer ----
                    //window.dataLayer = window.dataLayer || [];
                    //const items = carouselEl.querySelectorAll("[data-product-id], .card, .product");
                    //window.dataLayer.push({event: "reco_ready",placement: containerEl.getAttribute("data-placement") || null,item_count: items.length,duration_ms: durationMs,});

                    //console.info("[BCRecoObserver] Recommender ready", {recommenderName,skuCount: skuList.length,placement: containerEl.getAttribute("data-placement") || null,durationMs,});

                    // ---- Final call ----
                    if (typeof listingPagesLoaded === "function") {
                        listingPagesLoaded();
                    } else {
                        //console.warn("[BCRecoObserver] listingPagesLoaded() is missing");
                    }
                },
            });
        } else {
            console.error("[BCRecoObserver] init() not available. Did BCRecoObserver fail to load?");
        }


        this.setBrandsIds();
    }

    setBrandsIds() {
        fetch('/graphql', {
            method: 'POST',
            credentials: 'same-origin',
            headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${this.context.authorization}`
            },
            body: JSON.stringify({
                query: `query GetBrandsLinks {
                site {
                    popularBrands(first: 50) {
                        edges {
                            node {
                                entityId
                            }
                        }
                    }
                }
            }`
            })
        })
            .then(res => res.json())
            .then(data => {
                const brands = data.data.site.popularBrands.edges;
                for (let i = 0; i < brands.length; i++) {
                    brandsIds.push(brands[i].node.entityId);
                }
            })
            .catch(error => console.error(error));
    }
    
}
// if (['cednh71d7c', 'jbbwnmsgxh'].includes(window.BC_CONTEXT?.storeHash)) {
//     window.addEventListener('ss-on-results-load', () => {
//         if (typeof window.listingPagesLoaded === 'function') {
//             window.listingPagesLoaded();
//         }
//     });
// }